import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { getRecovTarget } from "@/lib/recovery";
import { startBurstCooldown, burstDemoFree } from "./AutoRun";

/* 🖥 Server burst — ×50/×100 seasons run on the VTS server, so they keep
   firing even when the app is closed. The panel polls live progress. */
export const useServerBurst = (game, onResult) => {
  const { session } = useAuth();
  const [st, setSt] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const prevRunning = useRef(false);
  const seenRef = useRef(null);      // last play index already animated/logged
  const startedRef = useRef(null);   // started_at of the burst being tracked
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const accountId = session?.account_id;

  const refresh = useCallback(async () => {
    if (!accountId) return null;
    try {
      const auth = session?.token ? { headers: { Authorization: `Bearer ${session.token}` } } : {};
      const { data } = await api.get(`/arcade/burst/status/${accountId}`, auth);
      setSt(data);
      // 🎬 live replay — surface every NEW server play so the game can animate
      // it and log it to the session history (old plays never replay on mount)
      const results = Array.isArray(data?.results) ? data.results : [];
      const maxI = results.length ? results[results.length - 1].i : (data?.i || 0);
      const startedAt = data?.started_at || null;
      if (seenRef.current === null) {
        seenRef.current = maxI;                 // mount baseline — never replay history
        startedRef.current = startedAt;
      } else {
        if (startedAt !== startedRef.current) { // 🆕 a NEW burst began — replay from play 1
          startedRef.current = startedAt;
          seenRef.current = 0;
        }
        if (maxI > seenRef.current) {
          const fresh = results.filter((r) => r.i > seenRef.current);
          seenRef.current = maxI;
          if (fresh.length) onResultRef.current?.(fresh, data);
        } else if (maxI < seenRef.current) {
          seenRef.current = maxI;               // index went backwards — re-baseline safely
        }
      }
      return data;
    } catch { return null; }
  }, [accountId, session?.token]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!st?.running) return undefined;
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [st?.running, refresh]);

  // season ended while we watch → reason-aware toast + 60-min break (real accounts)
  useEffect(() => {
    const r = !!st?.running;
    if (prevRunning.current && !r && st) {
      const w = st.w ?? 0, l = st.l ?? 0, net = st.net ?? 0;
      const sign = net >= 0 ? "+" : "−";
      const score = `${w}W / ${l}L · net ${sign}$${Math.abs(net).toFixed(2)}`;
      const reason = st.stopped_reason;
      if (reason === "ladder_paused") {
        toast.error("Bot paused after a full losing streak. Review before restarting.", { description: score, duration: 8000 });
      } else if (reason === "auth") {
        toast.error("Deriv login expired — the server burst stopped.", { description: "Reconnect your account and start the burst again.", duration: 8000 });
      } else if (reason === "user") {
        toast.info(`⏹ server burst stopped — ${score}`, { duration: 5000 });
      } else {
        if ((st.i || 0) >= (st.n || 0) && (st.n || 0) >= 50 && !burstDemoFree.on) {
          startBurstCooldown(st.game || game, 60);
        }
        toast.success(`🏁 server burst finished — ${score}`, { duration: 6000 });
      }
    }
    prevRunning.current = r;
  }, [st?.running]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useCallback(async ({ kind, side, n, stake, symbol, duration = 1 }) => {
    if (!session?.token || !accountId) {
      toast.error("Log in with Deriv first", { description: "Server bursts fire real contracts on your account — connect it to start." });
      return false;
    }
    let depth = 4, maxMult = 10;
    try {
      const d = parseInt(localStorage.getItem("vts_arcade_recov_cap"), 10);
      if (d >= 3 && d <= 6) depth = d;
      const m = parseInt(localStorage.getItem("vts_arcade_recov_maxmult"), 10);
      if (m >= 10 && m <= 500) maxMult = m;
    } catch { /* defaults */ }
    try {
      const { data } = await api.post("/arcade/burst/start", {
        account_id: accountId, access_token: session.token, game, kind, side, n,
        stake: Math.max(0.35, Number(stake) || 0.35), symbol: symbol || "1HZ100V",
        duration, depth_cap: depth, max_mult: maxMult, recovery_target: getRecovTarget(),
      });
      if (data?.ok === false) { toast.error(data.detail || "Couldn't start the server burst"); return false; }
      setSt(data);
      seenRef.current = 0;                       // 🆕 replay this burst's plays from #1
      startedRef.current = data?.started_at || null;
      setDismissed(false);
      toast.success(`🖥 ×${n} season running on the VTS server`, {
        description: "It keeps firing even if you close the app — come back anytime for the live score. Pauses itself after a full losing ladder.",
        duration: 7000,
      });
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the server burst");
      return false;
    }
  }, [session, accountId, game]);

  const stop = useCallback(async () => {
    if (!accountId) return;
    try {
      const auth = session?.token ? { headers: { Authorization: `Bearer ${session.token}` } } : {};
      await api.post(`/arcade/burst/stop/${accountId}`, {}, auth);
    } catch { /* status refresh shows truth */ }
    await refresh();
  }, [accountId, session?.token, refresh]);

  return { st, start, stop, refresh, running: !!st?.running, dismissed, dismiss: () => setDismissed(true) };
};

export const ServerBurstPanel = ({ srv, noun = "plays" }) => {
  const st = srv.st;
  if (!st || srv.dismissed) return null;
  const paused = !st.running && st.stopped_reason === "ladder_paused";
  const justDone = !st.running && st.stopped_reason === "done" && (st.i || 0) >= (st.n || 0);
  if (!st.running && !paused && !justDone) return null;
  const pct = Math.min(100, Math.round(((st.i || 0) / (st.n || 1)) * 100));
  const up = (st.net || 0) >= 0;
  return (
    <div data-testid="server-burst-panel" className={`rounded-lg border px-3 py-2.5 space-y-1.5 ${paused ? "border-[#F23645]/50 bg-[#F23645]/10" : "border-[#53b9ff]/40 bg-[#0B0E14]"}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-black font-mono text-[#53b9ff]">🖥 SERVER BURST · {st.label || st.game}</span>
        {st.running && <span className="text-[9px] font-mono text-[#089981] animate-pulse">LIVE — runs even if you close the app</span>}
        <button data-testid="server-burst-dismiss" onClick={srv.dismiss}
          className="ml-auto px-1.5 text-white/40 hover:text-white text-xs font-mono">✕</button>
      </div>
      <div className="flex items-center justify-between gap-2 text-[10px] font-mono font-black">
        <span className="text-[#F7B731]">⚡ {st.i}/{st.n} {noun} · {st.side}</span>
        <span className="text-white/55">✅ {st.w} · ❌ {st.l}</span>
        <span data-testid="server-burst-net" className={up ? "text-[#089981]" : "text-[#F23645]"}>
          {up ? "+" : "−"}${Math.abs(st.net || 0).toFixed(2)}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full rounded-full transition-[width] duration-500 ${up ? "bg-[#089981]" : "bg-[#F23645]"}`} style={{ width: `${pct}%` }} />
      </div>
      {st.running && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[9px] font-mono text-white/50">
            next stake ${Number(st.cur_stake || st.stake).toFixed(2)}{st.recovering ? " · 🔁 recovery" : " · base"} · {st.symbol}
          </span>
          <button data-testid="server-burst-stop" onClick={srv.stop}
            className="ml-auto px-2.5 py-1 rounded-md bg-[#F23645] text-white text-[10px] font-black font-mono">
            ⏹ STOP
          </button>
        </div>
      )}
      {paused && (
        <p data-testid="server-burst-paused" className="text-[10px] font-mono font-bold text-[#F23645]">
          Bot paused after a full losing streak. Review before restarting.
          <span className="block text-white/50 font-normal">The server burst stopped itself — it will NOT restart on its own.</span>
        </p>
      )}
      {justDone && (
        <p data-testid="server-burst-done" className="text-[10px] font-mono text-white/60">
          🏁 season complete on the server — every {noun.replace(/s$/, "")} is in your History.
        </p>
      )}
      {st.last_error && st.running && (
        <p className="text-[9px] font-mono text-[#F7B731]/80">⚠ {st.last_error} — retrying</p>
      )}
    </div>
  );
};
